// Shared "Frequency & Schedule" field rows.
//
// Both goal_resources_controller (per goal / barrier resource) and
// current_services_list_controller (per manually-added current service)
// let the user attach schedule rows of the shape
//   { id, dayType, times, unit: "times"|"session", hours, mins }
// with the SAME layout: dayType → # (× times / sessions) → hrs → min → ×.
//
// The markup structure, the DAY_TYPES option list, and the field wiring
// (data-field names + updateScheduleField / removeSchedule actions) live
// here so the two controllers can't drift. Each host passes:
//   controller : its Stimulus identifier (e.g. "goal-resources")
//   rowAttrs(s): the data-* attributes that identify a row for the
//                handlers (e.g. `data-resource-id="…" data-schedule-id="…"`)
//   cls        : the CSS class for each field, so each context keeps its
//                own theme (goal cards are light; the CS list is dark)
//   esc        : the host's HTML escaper

export const DAY_TYPES = [
  "Daily", "Mon-Fri", "Sat-Sun", "Non-School",
  "Weekly", "Bi-Weekly", "Monthly", "Quarterly",
  "Semi-Annually", "Annually", "As Needed",
  "Mon-Wed-Fri", "Tue-Thu", "Mon-Wed", "Tue-Thu-Sat",
  "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"
]

const escHtml = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;")

// "To be determined" flags — when any is checked the row has no fixed hours;
// the hrs/min inputs are replaced by the tag(s), everywhere the schedule shows
// (editor rows, unmet needs, current services, PDF/preview).
export const SCHEDULE_TBD_FLAGS = [
  { field: "assessmentNeeded",     label: "Assessment needed" },
  { field: "weeklyScheduleNeeded", label: "Weekly Schedule needed" },
]
// The combined tag text for a schedule row, or "" when it has fixed hours.
export function scheduleTbdTag(s) {
  if (!s) return ""
  return SCHEDULE_TBD_FLAGS.filter(f => s[f.field]).map(f => f.label).join(" · ")
}

// ===== Canonical schedule → text formatting =====
// Every section (editor rows, unmet needs, current services, cs-suggestions,
// PDF/preview) MUST format schedule rows through these so they never drift.

// Duration + count for one row: "2× 3 hrs 30 min", "1 session", or the TBD tag.
export function scheduleTimeText(s) {
  const tbd = scheduleTbdTag(s)
  if (tbd) return tbd
  const h = parseFloat(s && s.hours) || 0
  const m = parseFloat(s && s.mins)  || 0
  const t = parseFloat(s && s.times) || 0
  const isSession = (s && s.unit) === "session"
  const n = t > 0 ? t : 1
  if (!h && !m) return isSession ? `${n} ${n === 1 ? "session" : "sessions"}` : `${n} ${n === 1 ? "time" : "times"}`
  let base = (h && m) ? `${h} hrs ${m} min` : m ? `${m} min` : `${h} hrs`
  if (isSession) return `${n} ${n === 1 ? "session" : "sessions"} ${base}`
  if (t > 1) base = `${t}× ${base}`
  return base
}

const SCHED_UNIT_SUFFIX = {
  "Weekly": "/wk", "Bi-Weekly": "/biwk", "Monthly": "/mo", "Quarterly": "/qtr",
  "Semi-Annually": "/6mo", "Annually": "/yr", "As Needed": ""
}
// Per-period suffix ("/wk", …); empty for TBD rows. Accepts an item or a dayType.
export function scheduleUnitText(s) {
  if (scheduleTbdTag(s)) return ""
  const dayType = (s && typeof s === "object") ? s.dayType : s
  return SCHED_UNIT_SUFFIX[dayType] || "/day"
}

// Cost-aware chip body: "$120/wk" (cost mode) else "2× 3 hrs/wk" else the TBD tag.
export function scheduleChipBody(s) {
  const tbd = scheduleTbdTag(s)
  if (tbd) return tbd
  const costStr = s && s.cost != null ? String(s.cost).replace(/[^\d.]/g, "") : ""
  const costVal = costStr === "" ? NaN : parseFloat(costStr)
  const hasCost = !isNaN(costVal) && costVal > 0
  if (s && (s.mode === "cost" || hasCost)) {
    const v = isNaN(costVal) ? 0 : costVal
    const pretty = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 })
    return `$${pretty}${scheduleUnitText(s)}`
  }
  return `${scheduleTimeText(s)}${scheduleUnitText(s)}`
}

// True when a row should be shown (has hours/mins/times or a TBD tag).
export function scheduleHasContent(s) {
  return !!(s && (s.hours || s.mins || s.times || scheduleTbdTag(s)))
}

// Renders the schedule rows for `schedule`. Returns "" for an empty list.
export function scheduleRowsHtml(schedule, { controller, rowAttrs, cls = {}, esc = escHtml }) {
  const upd = `input->${controller}#updateScheduleField`
  const chg = `change->${controller}#updateScheduleField`
  const del = `${controller}#removeSchedule`
  return (Array.isArray(schedule) ? schedule : []).map(s => {
    const a = rowAttrs(s)
    const tbd = scheduleTbdTag(s)
    // When a TBD flag is checked, the hrs/min inputs are replaced by the tag(s).
    const hoursOrTag = tbd
      ? `<span style="display:inline-flex;align-items:center;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;color:#b45309;background:#fffbeb;border:1px solid #fde68a;white-space:nowrap;">${esc(tbd)}</span>`
      : `<input type="text" placeholder="hrs" value="${esc(s.hours || "")}"
                data-action="${upd}" ${a} data-field="hours" class="${cls.hrs || ""}">
         <span class="${cls.lbl || ""}">hrs</span>
         <input type="text" placeholder="min" value="${esc(s.mins || "")}"
                data-action="${upd}" ${a} data-field="mins" class="${cls.min || ""}">
         <span class="${cls.lbl || ""}">min</span>`
    const flags = SCHEDULE_TBD_FLAGS.map(f => {
      const on = !!s[f.field]
      return `<label style="display:inline-flex;align-items:center;gap:3px;font-size:10px;white-space:nowrap;cursor:pointer;font-weight:${on ? "700" : "500"};color:${on ? "#b45309" : "#6b7280"};"
                     title="Support to be determined — no fixed hours">
                <input type="checkbox" data-action="${chg}" ${a} data-field="${f.field}" ${on ? "checked" : ""}>
                ${esc(f.label)}
              </label>`
    }).join("")
    return `
      <div class="${cls.row || ""}">
        <select data-action="${chg}" ${a} data-field="dayType" class="${cls.daytype || ""}">
          ${DAY_TYPES.map(d => `<option value="${esc(d)}" ${s.dayType === d ? "selected" : ""}>${esc(d)}</option>`).join("")}
        </select>
        <input type="text" placeholder="#" value="${esc(s.times || "")}"
               data-action="${upd}" ${a} data-field="times" class="${cls.num || ""}">
        <select data-action="${chg}" ${a} data-field="unit"
                title="Switch between times (×) and sessions" class="${cls.unit || ""}">
          <option value="times"   ${(s.unit || "times") === "times" ? "selected" : ""}>× times</option>
          <option value="session" ${s.unit === "session" ? "selected" : ""}>sessions</option>
        </select>
        ${hoursOrTag}
        <span style="display:inline-flex;flex-wrap:wrap;gap:2px 10px;align-items:center;">${flags}</span>
        <button type="button" data-action="${del}" ${a}
                class="${cls.del || ""}"
                title="Remove schedule row" aria-label="Remove schedule row">&times;</button>
      </div>`
  }).join("")
}
